import React, { useEffect, useMemo, useState } from "react";
import "./Clientes.css";
import "../styles/theme-dark.css";
import EmailPresupuestoModal from "./EmailPresupuestoModal";
import { useNavigate, useLocation } from "react-router-dom";


const API = "/api";


/* ============================================================
   HELPERS DE TEXTO / NÚMEROS (UNIFICADO)
============================================================ */

// Convierte número a texto estilo ES (21.000,00)
function euro(num) {
  const n = Number(num) || 0;
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Convierte texto → número real
// Acepta: 21000, 21.000, 21.000,00, 21.000,00 €, etc.
function parseEuro(value) {
  if (!value) return 0;
  const cleaned = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  return Number(cleaned) || 0;
}

// Normalizador para búsqueda
const norm = (s = "") =>
  (s + "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Formateo de euro para la TABLA (igual que euro())
const formatEuro = (number) => euro(number) + " €";

// Badges pequeños
const SmallBadge = ({ label, color, bg }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 6px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      backgroundColor: bg,
      color,
      border: `1px solid ${color}30`,
      marginRight: 4,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

// Estado principal del presupuesto
const EstadoAdminBadge = ({ estado }) => {
  let key = (estado || "pendiente").toLowerCase();
  if (key === "pagado") key = "aceptado";

  const map = {
    pendiente: {
      label: "Pendiente",
      color: "#FACC15",
      bg: "#FACC1530",
    },
    aceptado: {
      label: "Aceptado",
      color: "#34D399",
      bg: "#34D39930",
    },
    facturado: {
      label: "Facturado",
      color: "#A78BFA",
      bg: "#A78BFA30",
    },
    rechazado: {
      label: "Rechazado",
      color: "#F97373",
      bg: "#F9737330",
    },
  };

  const cfg = map[key] || map.pendiente;
  return <SmallBadge {...cfg} />;
};

// Estado de pago
const EstadoPagoBadge = ({ total, pagos }) => {
  const t = Number(total) || 0;
  const pagado = (pagos || []).reduce(
    (acc, p) => acc + (Number(p.importe) || 0),
    0
  );

  let label = "Sin pagar";
  let color = "#9CA3AF";
  let bg = "#9CA3AF30";

  if (t > 0 && pagado > 0 && pagado + 0.01 < t) {
    label = "Parcial";
    color = "#F59E0B";
    bg = "#F59E0B30";
  } else if (t > 0 && pagado + 0.01 >= t) {
    label = "Pagado";
    color = "#4ADE80";
    bg = "#4ADE8030";
  }

  return <SmallBadge label={label} color={color} bg={bg} />;
};

/* ============================================================
   COMPONENTE PRINCIPAL
============================================================ */

export default function Presupuestos() {
  const [list, setList] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [company, setCompany] = useState(null);
  const [paymentPresets, setPaymentPresets] = useState([]);
  const [openEmail, setOpenEmail] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [includeIban, setIncludeIban] = useState(true);
  const isEditing = editingId !== null;
  const [pdfTipo, setPdfTipo] = useState("presupuesto");


  const todayStr = new Date().toISOString().slice(0, 10);

  const [orden, setOrden] = useState("fecha-desc");
  const navigate = useNavigate();
  const location = useLocation();



  // Estructura vacía de presupuesto
  const empty = {
    cliente_id: "",
    fecha: todayStr,
    iva_pct: 21,
    items: [],
    formas_pago: [],
    pagos_internos: [],
    estado: "pendiente",
  };
  const [form, setForm] = useState(empty);

  const [query, setQuery] = useState("");

  /* ============================================================
     LOAD INICIAL
  ============================================================ */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);

      const safeJson = async (res) =>
        res.ok ? res.json() : { success: false, data: [] };

      try {
        const [p, c, m, comp, presets, email] = await Promise.all([
          fetch(`${API}/presupuestos`).then(safeJson),
          fetch(`${API}/clientes`).then(safeJson),
          fetch(`${API}/maquinas`).then(safeJson),
          fetch(`${API}/companies`).then(safeJson),
          fetch(`${API}/payment-presets`).then((r) => r.json()),
          fetch(`${API}/email-settings`).then(safeJson),
        ]);

        setList(p.data || []);
        setClientes(c.data || []);
        setMaquinas(m.data || []);
        setCompany(comp.data?.[0] || null);
        setPaymentPresets(Array.isArray(presets) ? presets : []);
        setEmailSettings(email.data || null);
      } catch (e) {
        setError("No se pudo cargar los presupuestos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
  const openId = location.state?.openId;
  if (!openId || list.length === 0) return;

  const p = list.find((x) => String(x.id) === String(openId));
  if (p) {
    openEdit(p);
    navigate("/presupuestos", { replace: true });
  }
}, [location.state, list]);


  const clienteById = (id) =>
    clientes.find((c) => String(c.id) === String(id));

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  /* ============================================================
     TOTALES PARA EL MODAL
  ============================================================ */
  const totales = useMemo(() => {
    const base = (form.items || []).reduce(
      (acc, it) =>
        acc + parseEuro(it.price) * (Number(it.qty) || 1),
      0
    );
    const iva = base * (Number(form.iva_pct) / 100);
    return {
      base: round2(base),
      iva: round2(iva),
      total: round2(base + iva),
    };
  }, [form.items, form.iva_pct]);

  const pagosInternosTotal = useMemo(
    () =>
      (form.pagos_internos || []).reduce(
        (acc, p) => acc + parseEuro(p.importe),
        0
      ),
    [form.pagos_internos]
  );

  /* ============================================================
     DUPLICAR
  ============================================================ */
  function duplicateBudget(p) {
    if (!window.confirm(`¿Duplicar el presupuesto ${p.numero}?`)) return;

    const todayStr = new Date().toISOString().slice(0, 10);

    const duplicated = {
      cliente_id: p.clienteId || p.cliente_id || "",
      fecha: todayStr,
      iva_pct: p.iva_pct || p.iva || 21,
      items: Array.isArray(p.items)
        ? p.items.map((item) => ({
            tituloNegrita: item.tituloNegrita || "",
            descripcion: item.descripcion || "",
            qty: Number(item.qty) || 1,
            price: euro(item.price), // Formateado
          }))
        : [],
      formas_pago: Array.isArray(p.formas_pago) ? [...p.formas_pago] : [],
      pagos_internos: Array.isArray(p.pagos_internos)
        ? p.pagos_internos.map((pi) => ({
            fecha: todayStr,
            importe: euro(pi.importe),
            nota: pi.nota || pi.concepto || "",
          }))
        : [],
      estado: "pendiente",
    };

    setForm(duplicated);
    setEditingId(null);
    setIncludeIban(true);
    setShow(true);
  }

  

  /* ============================================================
     FILTRADO + ORDEN
  ============================================================ */
  const filtered = useMemo(() => {
    let arr = [...list];

    // Búsqueda sencilla
    if (query.trim()) {
      const q = norm(query);
      arr = arr.filter((p) => {
        const cliente = clienteById(p.clienteId || p.cliente_id);
        return (
          norm(p.numero || "").includes(q) ||
          norm(p.fecha || "").includes(q) ||
          norm(cliente?.nombre_empresa || "").includes(q) ||
          norm(p.estado || "").includes(q)
        );
      });
    }

    arr.sort((a, b) => {
      const numA = Number(a.numero?.replace(/\D/g, "") || a.id);
      const numB = Number(b.numero?.replace(/\D/g, "") || b.id);
      const fechaA = new Date(a.fecha).getTime();
      const fechaB = new Date(b.fecha).getTime();

      switch (orden) {
        case "fecha-asc":
          return fechaA - fechaB;
        case "fecha-desc":
          return fechaB - fechaA;
        case "numero-asc":
          return numA - numB;
        case "numero-desc":
          return numB - numA;
        default:
          return 0;
      }
    });

    return arr;
  }, [list, query, orden, clientes]);



  /* ============================================================
     FUNCIONES DEL FORMULARIO (EDITAR / CREAR)
  ============================================================ */

  const openCreate = () => {
    setForm(empty);
    setIncludeIban(true);
    setEditingId(null);
    setShow(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      cliente_id: p.clienteId || p.cliente_id || "",
      fecha: p.fecha ?? todayStr,
      iva_pct: p.iva_pct ?? p.iva ?? 21,
      items: Array.isArray(p.items)
        ? p.items.map((item) => ({
            ...item,
            price: euro(item.price),
            qty: Number(item.qty) || 1,
          }))
        : [],
      formas_pago: Array.isArray(p.formas_pago) ? p.formas_pago : [],
      pagos_internos: Array.isArray(p.pagos_internos)
        ? p.pagos_internos.map((pago) => ({
            fecha: todayStr,
            importe: euro(pago.importe),
            nota: pago.concepto || pago.nota || "",
          }))
        : [],
      estado: p.estado || "pendiente",
    });
    setIncludeIban(p.include_iban !== false);
    setShow(true);
  };

  const close = () => {
    if (!saving) {
      setShow(false);
      setEditingId(null);
    }
  };

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onChangeNumber = (e) =>
    setForm((prev) => ({
      ...prev,
      [e.target.name]: parseEuro(e.target.value),
    }));

  const addManualItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { tituloNegrita: "", descripcion: "", qty: 1, price: euro(0) },
      ],
    }));
  };

  const addFromMachine = (machineId) => {
    const m = maquinas.find((x) => String(x.id) === String(machineId));
    if (!m) return;

    const title = [m.nombre, m.modelo].filter(Boolean).join(" ");
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          tituloNegrita: title,
          descripcion: m.descripcion || "",
          qty: 1,
          price: euro(m.precio),
        },
      ],
    }));
  };

  const updateItem = (i, patch) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[i] = { ...items[i], ...patch };
      return { ...prev, items };
    });
  };

  const removeItem = (i) => {
    setForm((prev) => {
      const items = [...prev.items];
      items.splice(i, 1);
      return { ...prev, items };
    });
  };

  /* ============================================================
     FORMAS DE PAGO
  ============================================================ */

  const addFormaPago = () => {
    setForm((prev) => ({
      ...prev,
      formas_pago: [...prev.formas_pago, ""],
    }));
  };

  const updatePago = (i, val) => {
    setForm((prev) => {
      const fp = [...prev.formas_pago];
      fp[i] = val;
      return { ...prev, formas_pago: fp };
    });
  };

  const removeFormaPago = (i) => {
    setForm((prev) => {
      const fp = [...prev.formas_pago];
      fp.splice(i, 1);
      return { ...prev, formas_pago: fp };
    });
  };

  /* ============================================================
     PAGOS INTERNOS
  ============================================================ */

  const addPagoInterno = () => {
    setForm((prev) => ({
      ...prev,
      pagos_internos: [
        ...prev.pagos_internos,
        { fecha: todayStr, importe: euro(0), nota: "" },
      ],
    }));
  };

  const updatePagoInterno = (i, patch) => {
    setForm((prev) => {
      const arr = [...prev.pagos_internos];
      arr[i] = { ...arr[i], ...patch };
      return { ...prev, pagos_internos: arr };
    });
  };

  const removePagoInterno = (i) => {
    setForm((prev) => {
      const arr = [...prev.pagos_internos];
      arr.splice(i, 1);
      return { ...prev, pagos_internos: arr };
    });
  };

  /* ============================================================
     ACEPTAR - LÓGICA DE ESTADOS
  ============================================================ */

  const buildEstadoConPagos = (estadoActual, total, pagosTotal) => {
    let estado = estadoActual || "pendiente";
    if (estado === "pendiente" && pagosTotal > 0) {
      estado = "aceptado";
    }
    return estado;
  };

  /* ============================================================
     CREAR PRESUPUESTO
  ============================================================ */

  async function createBudget(e) {
    e.preventDefault();
    if (!form.cliente_id) return alert("Selecciona un cliente");

    setSaving(true);
    try {
      const estado = buildEstadoConPagos(
        form.estado,
        totales.total,
        pagosInternosTotal
      );

      const payload = {
        cliente_id: form.cliente_id,
        fecha: form.fecha,
        iva_pct: form.iva_pct,
        items: form.items.map((it) => ({
          tituloNegrita: it.tituloNegrita,
          descripcion: it.descripcion,
          qty: Number(it.qty),
          price: parseEuro(it.price),
        })),
        formas_pago: form.formas_pago,
        pagos_internos: (form.pagos_internos || []).map((p) => ({
          fecha: p.fecha,
          importe: parseEuro(p.importe),
          concepto: p.nota || "",
        })),
        estado,
        include_iban: includeIban,
        total_base: totales.base,
        total_iva: totales.iva,
        total_total: totales.total,
      };

      const res = await fetch(`${API}/presupuestos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json?.success) {
        setList((prev) => [json.data, ...prev]);
        openEdit(json.data);
      } else {
        alert("No se pudo crear el presupuesto");
      }
    } catch (e) {
      alert("Error creando presupuesto");
    } finally {
      setSaving(false);
    }
  }

  /* ============================================================
     GUARDAR CAMBIOS (PUT NORMAL)
  ============================================================ */

  async function saveChanges(e) {
    e?.preventDefault?.();
    if (!isEditing) return;

    setSaving(true);

    try {
      const estado = buildEstadoConPagos(
        form.estado,
        totales.total,
        pagosInternosTotal
      );

      const payload = {
        cliente_id: form.cliente_id,
        fecha: form.fecha,
        iva_pct: form.iva_pct,
        items: form.items.map((it) => ({
          tituloNegrita: it.tituloNegrita,
          descripcion: it.descripcion,
          qty: Number(it.qty),
          price: parseEuro(it.price),
        })),
        formas_pago: form.formas_pago,
        pagos_internos: (form.pagos_internos || []).map((p) => ({
          fecha: p.fecha,
          importe: parseEuro(p.importe),
          concepto: p.nota || "",
        })),
        estado,
        include_iban: includeIban,
        total_base: totales.base,
        total_iva: totales.iva,
        total_total: totales.total,
      };

      const res = await fetch(`${API}/presupuestos/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (json?.success) {
        setList((prev) =>
          prev.map((x) =>
            String(x.id) === String(editingId) ? json.data : x
          )
        );
        setForm((prev) => ({ ...prev, estado }));
      } else {
        alert("No se pudo guardar");
      }
    } catch (e) {
      alert("Error guardando");
    } finally {
      setSaving(false);
    }
  }

  /* ============================================================
     FACTURAR — BLOQUE NUEVO COMPLETO
  ============================================================ */

  const puedeFacturar = (p) =>
    (p.estado === "aceptado" || p.estado === "pagado") &&
    p.estado !== "facturado";

  async function crearFactura(presupuesto) {
    if (!puedeFacturar(presupuesto))
      return alert("Solo se pueden facturar aceptados o pagados.");

    if (!window.confirm(`¿Crear factura desde ${presupuesto.numero}?`))
      return;

    try {
      // 1️⃣ Calcular base/iva/total desde líneas REALES
      const items = Array.isArray(presupuesto.items)
        ? presupuesto.items
        : [];

      const base = items.reduce((acc, it) => {
        const qty = Number(it.qty ?? 0);
        const price = Number(it.price ?? parseEuro(it.price) ?? 0);
        return acc + qty * price;
      }, 0);

      const ivaPct =
        Number(presupuesto.iva_pct) ||
        Number(presupuesto.iva) ||
        21;

      const iva = base * (ivaPct / 100);
      const total = base + iva;

      // 2️⃣ Pagos internos reales
      const pagado = (presupuesto.pagos_internos || []).reduce(
        (acc, pi) => acc + (Number(pi.importe) || parseEuro(pi.importe) || 0),
        0
      );

      const estaPagado = total > 0 && pagado + 0.01 >= total;

      // 3️⃣ Datos de factura
      const facturaData = {
        clienteId: Number(presupuesto.clienteId || presupuesto.cliente_id),
        empresaId: 1,
        items: items.map((it) => ({
          tituloNegrita: it.tituloNegrita || "",
          descripcion: it.descripcion || "",
          qty: Number(it.qty) || 0,
          price: Number(it.price) || parseEuro(it.price) || 0,
        })),
        formasPago: presupuesto.formas_pago || [],
        baseImponible: base,
        iva,
        total,
        notas: `Factura generada desde presupuesto ${presupuesto.numero}`,
        estado: estaPagado ? "pagado" : "pendiente",
        fechaVencimiento: new Date(Date.now() + 30 * 86400000)
          .toISOString()
          .split("T")[0],
      };

      const res = await fetch(`${API}/facturas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(facturaData),
      });

      if (!res.ok) throw new Error("Error creando factura");

      await res.json();

      // 4️⃣ Marcar presupuesto como facturado (recalcula en backend)
      const updateRes = await fetch(
        `${API}/presupuestos/${presupuesto.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ facturar: true }),
        }
      );

      const updatedJson = await updateRes.json();

      if (updateRes.ok && updatedJson?.data) {
        setList((prev) =>
          prev.map((p) =>
            String(p.id) === String(presupuesto.id)
              ? updatedJson.data
              : p
          )
        );
      }

      alert(`Factura creada correctamente.`);
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  function facturarDesdeModal() {
    if (!isEditing) return;
    const p = list.find((x) => String(x.id) === String(editingId));
    if (!p) return alert("Guarda antes de facturar");
    crearFactura(p);
  }
  /* ============================================================
     DELETE
  ============================================================ */

  async function remove(id, estado) {
    if (estado === "facturado") {
      const seguro = window.confirm(
        "⚠️ Este presupuesto está FACTURADO.\n\n¿Seguro que deseas eliminarlo igualmente?"
      );
      if (!seguro) return;
    } else {
      if (!window.confirm("¿Eliminar presupuesto?")) return;
    }

    try {
      const res = await fetch(`${API}/presupuestos/${id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (json?.success) {
        setList((prev) => prev.filter((p) => String(p.id) !== String(id)));
      } else {
        alert("No se pudo eliminar");
      }
    } catch (e) {
      alert("Error eliminando");
    }
  }

  /* ============================================================
     PDF
  ============================================================ */

  function downloadPdf() {
  if (!isEditing) return alert("Primero guarda el presupuesto");

  const tipo = window.confirm(
    "Aceptar → PRESUPUESTO\nCancelar → PROFORMA"
  )
    ? "presupuesto"
    : "proforma";

  setPdfTipo(tipo);

  window.open(
    `${API}/presupuestos/${editingId}/pdf?tipo=${tipo}`,
    "_blank"
  );
}


  /* ============================================================
     ESTILOS (NO MODIFICAR)
  ============================================================ */

  const localCss = `
    .presupuestos-modal select,
    .presupuestos-modal select option {
      background: #020617 !important;
      color: #F9FAFB !important;
    }
    .presupuestos-modal .items-head,
    .presupuestos-modal .items-row,
    .presupuestos-modal .items-empty {
      background: rgba(30,41,59,0.9) !important;
      color: #F9FAFB !important;
    }
    .presupuestos-modal .items-table {
      border-radius: 14px;
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.1);
    }
    .presupuestos-modal textarea,
    .presupuestos-modal input {
      background: rgba(15,23,42,0.95) !important;
      color: #F9FAFB !important;
    }
    .presupuestos-modal .modal-footer {
      background: rgba(15,23,42,0.85) !important;
      border-top: 1px solid rgba(255,255,255,0.12);
    }
    .presupuestos-modal ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    .presupuestos-modal ::-webkit-scrollbar-track {
      background: rgba(15,23,42,0.8);
    }
    .presupuestos-modal ::-webkit-scrollbar-thumb {
      background: rgba(148,163,184,0.8);
      border-radius: 999px;
    }
    .presupuestos-modal {
      width: 95vw !important;
      max-width: 2500px !important;
    }
  `;

  /* ============================================================
     RENDER
  ============================================================ */

  if (loading) return <div style={{ padding: 20 }}>Cargando…</div>;
  if (error)
    return <div style={{ padding: 20, color: "#fca5a5" }}>{error}</div>;

  return (
    <>
      <style>{localCss}</style>

      {/* MODAL EMAIL */}
<EmailPresupuestoModal
  isOpen={openEmail}
  onClose={() => setOpenEmail(false)}
  presupuesto={emailData}
  empresa={company}
  emailSettings={emailSettings}
  onSend={async ({ to, subject, html }) => {
    try {
 const res = await fetch(
  `${API}/presupuestos/${emailData.id}/send-email?tipo=${emailData.tipo || "presupuesto"}`,
  {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      to,
      subject,
      html,
    }),
  }
);


      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "No se pudo enviar el correo");
      }

      alert("Email enviado correctamente");
      setOpenEmail(false);
    } catch (e) {
      alert("Error: " + e.message);
    }
  }}
/>


      <div className="page-dark">
        {/* HEADER */}
        <header
          style={{
            padding: "20px 30px",
            marginBottom: 25,
            background: "rgba(15,23,42,0.65)",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
            borderRadius: "0 0 16px 16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <img src="/corex-logo.png" alt="COREX" style={{ height: 40 }} />
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                Presupuestos
              </h1>
            </div>

            <button
              className="glass-btn"
              onClick={() => navigate("/dashboard")}

              style={{ fontSize: 14 }}
            >
              ← Volver
            </button>
          </div>

          <p style={{ opacity: 0.7, marginTop: 6 }}>
            Crea, gestiona y controla el estado de tus presupuestos.
          </p>
        </header>

        {/* BUSCADOR Y NUEVO */}
        <div
          className="glass-box"
          style={{
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <input
              className="glass-input"
              type="search"
              placeholder="Buscar…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div
              style={{
                fontSize: 12,
                marginTop: 4,
                opacity: 0.6,
              }}
            >
              Total: <strong>{list.length}</strong> — Coincidencias:{" "}
              <strong>{filtered.length}</strong>
            </div>
          </div>

          {/* ORDEN */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 180,
            }}
          >
            <label style={{ fontSize: 12, opacity: 0.7 }}>Ordenar por</label>
            <select
              className="glass-input"
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
            >
              <option value="fecha-desc">Fecha (más reciente)</option>
              <option value="fecha-asc">Fecha (más antigua)</option>
              <option value="numero-desc">Número (desc)</option>
              <option value="numero-asc">Número (asc)</option>
            </select>
          </div>

          <button className="glass-btn" onClick={openCreate}>
            + Nuevo Presupuesto
          </button>
        </div>

        {/* TABLA */}
        <div className="glass-box">
          <table className="glass-table compact-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Pagos</th>
                <th>Base</th>
                <th>IVA</th>
                <th>Total</th>
                <th>Pag.Int</th>
                <th>Pendiente</th>
                <th style={{ width: 260, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((p) => {
                const cliente = clienteById(p.clienteId || p.cliente_id);

                const totalRow = Number(p.total ?? 0);
                const pagosInternosRow = (p.pagos_internos || []).reduce(
                  (acc, pi) => acc + (Number(pi.importe) || 0),
                  0
                );
                const pendienteRow = round2(totalRow - pagosInternosRow);

                return (
                  <tr key={p.id}>
                    <td>
                      <strong style={{ fontSize: 12 }}>
                        {p.numero || p.id}
                      </strong>
                    </td>

                    <td style={{ fontSize: 12 }}>{p.fecha}</td>

                    <td
                      style={{
                        fontSize: 12,
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cliente?.nombre_empresa || "-"}
                    </td>

                    <td style={{ fontSize: 11 }}>
                      <EstadoAdminBadge estado={p.estado} />
                    </td>

                    <td style={{ fontSize: 11 }}>
                      <EstadoPagoBadge
                        total={totalRow}
                        pagos={p.pagos_internos}
                      />
                    </td>

                    <td style={{ fontSize: 12, textAlign: "right" }}>
                      {formatEuro(p.totalBase ?? 0)}
                    </td>

                    <td style={{ fontSize: 12, textAlign: "right" }}>
                      {formatEuro(p.totalIva ?? 0)}
                    </td>

                    <td
                      style={{
                        fontSize: 12,
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      {formatEuro(totalRow ?? 0)}
                    </td>

                    <td
                      style={{
                        fontSize: 12,
                        textAlign: "right",
                        opacity: 0.7,
                      }}
                    >
                      {formatEuro(pagosInternosRow)}
                    </td>

                    <td
                      style={{
                        fontSize: 12,
                        textAlign: "right",
                        color: pendienteRow > 0 ? "#f87171" : "#4ade80",
                      }}
                    >
                      {formatEuro(pendienteRow)}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <div className="action-buttons-compact">
                        <button
  className="glass-btn icon-btn"
  onClick={() => {
    setEmailData({
      ...p,
      cliente_email:
        clienteById(
          p.clienteId || p.cliente_id
        )?.email || "",
      tipo: pdfTipo, // 🔥 PRESUPUESTO o PROFORMA
    });
    setOpenEmail(true);
  }}
  title="Enviar email"
>
  ✉
</button>


                        <button
                          className="glass-btn icon-btn"
                          onClick={() => duplicateBudget(p)}
                          title="Duplicar"
                        >
                          📋
                        </button>

                        <button
                          className="glass-btn icon-btn"
                          onClick={() => openEdit(p)}
                          title="Editar"
                        >
                          ✏️
                        </button>

                        <button
                          className="glass-btn icon-btn"
                          onClick={() =>
                            window.open(
                              `${API}/presupuestos/${p.id}/pdf`,
                              "_blank"
                            )
                          }
                          title="PDF"
                        >
                          📄
                        </button>

                        <button
  className="glass-btn icon-btn"
  onClick={() =>
    navigate(`/presupuestos/${p.id}/adjuntar-visita`)
  }
  title="Adjuntar a visita"
>
  📎
</button>




                        <button
                          className="glass-btn icon-btn"
                          onClick={() => remove(p.id, p.estado)}
                          style={{ background: "rgba(239,68,68,0.2)" }}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      opacity: 0.6,
                    }}
                  >
                    Sin resultados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL */}
        {show && (
          <div className="modal-overlay">
            <div
              className="modal-content modal--tall presupuestos-modal"
              style={{
                background: "rgba(15,23,42,0.85)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(12px)",
                borderRadius: 16,
                color: "#F9FAFB",
                width: "90vw",
                maxWidth: "1400px",
                height: "90vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* HEADER */}
              <div
                className="modal-header sticky-top"
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(15,23,42,0.65)",
                  backdropFilter: "blur(6px)",
                  padding: "14px 20px",
                  zIndex: 10,
                }}
              >
                <h3 style={{ margin: 0 }}>
                  {isEditing ? "Editar Presupuesto" : "Nuevo Presupuesto"}
                </h3>

                <button className="glass-btn" onClick={close}>
                  ✕
                </button>
              </div>

              {/* ESTADO + PAGOS */}
              <div
                style={{
                  display: "flex",
                  gap: 24,
                  padding: "12px 20px 0",
                  flexShrink: 0,
                  background: "rgba(15,23,42,0.65)",
                  backdropFilter: "blur(4px)",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>Estado</div>
                  <EstadoAdminBadge estado={form.estado} />
                </div>

                <div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>Pagos</div>
                  <EstadoPagoBadge total={totales.total} pagos={form.pagos_internos} />
                </div>
              </div>

              {/* CONTENIDO DEL FORMULARIO */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "20px",
                }}
              >
                <form
                  className="modal-body form-grid compact"
                  onSubmit={isEditing ? saveChanges : createBudget}
                >
                  {/* CAMPOS PRINCIPALES */}
                  <div className="grid-3">
                    <div className="field">
                      <label>Cliente *</label>
                      <select
                        name="cliente_id"
                        value={form.cliente_id}
                        onChange={onChange}
                        required
                        className="glass-input"
                      >
                        <option value="">Selecciona…</option>
                        {clientes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre_empresa}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label>Fecha</label>
                      <input
                        type="date"
                        name="fecha"
                        value={form.fecha}
                        onChange={onChange}
                        className="glass-input"
                      />
                    </div>

                    <div className="field">
                      <label>IVA (%)</label>
                      <input
                        type="text"
                        name="iva_pct"
                        value={form.iva_pct}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            iva_pct: parseEuro(e.target.value),
                          }))
                        }
                        className="glass-input"
                      />
                    </div>
                  </div>

                  {/* ITEMS */}
                  <div className="section-head">
                    <div className="section-title">Conceptos</div>

                    <div className="inline-actions">
                      <select
                        className="glass-input"
                        onChange={(e) =>
                          e.target.value &&
                          (addFromMachine(e.target.value),
                          (e.target.value = ""))
                        }
                      >
                        <option value="">Añadir desde máquina…</option>
                        {maquinas.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.nombre} {m.modelo ? `(${m.modelo})` : ""} —{" "}
                            {formatEuro(m.precio)}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="glass-btn"
                        onClick={addManualItem}
                      >
                        + Línea manual
                      </button>
                    </div>
                  </div>

                  <div className="section-block">
                    <div className="items-table">
                      <div className="items-head">
                        <div className="col-title">Encabezado</div>
                        <div className="col-desc">Descripción</div>
                        <div className="col-qty">Ud.</div>
                        <div className="col-price">Precio</div>
                        <div className="col-actions" />
                      </div>

                      {(form.items || []).map((it, i) => (
                        <div className="items-row" key={i}>
                          <div className="col-title">
                            <input
                              className="glass-input"
                              value={it.tituloNegrita}
                              onChange={(e) =>
                                updateItem(i, {
                                  tituloNegrita: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-desc">
                            <textarea
                              className="glass-input"
                              rows={2}
                              value={it.descripcion}
                              onChange={(e) =>
                                updateItem(i, {
                                  descripcion: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-qty">
                            <input
                              type="text"
                              className="glass-input"
                              value={it.qty}
                              onChange={(e) =>
                                updateItem(i, {
                                  qty: parseEuro(e.target.value),
                                })
                              }
                            />
                          </div>

                          <div className="col-price">
                            <input
                              type="text"
                              className="glass-input"
                              style={{ textAlign: "right" }}
                              value={it.price}
                              onChange={(e) =>
                                updateItem(i, {
                                  price: euro(parseEuro(e.target.value)),
                                })
                              }
                            />
                            <span className="currency">€</span>
                          </div>

                          <div className="col-actions">
                            <button
                              type="button"
                              className="glass-btn"
                              onClick={() => removeItem(i)}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}

                      {form.items.length === 0 && (
                        <div className="items-empty">
                          Sin líneas. Añade desde máquina o crea una manual.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* FORMAS DE PAGO */}
                  <div className="section-head">
                    <div className="section-title">Formas de pago</div>
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={addFormaPago}
                    >
                      + Añadir forma de pago
                    </button>
                  </div>

                  <datalist id="payment-presets">
                    {paymentPresets.map((p) => (
                      <option
                        key={p.id}
                        value={p.descripcion || p.nombre}
                        label={p.nombre}
                      />
                    ))}
                  </datalist>

                  {form.formas_pago.map((fp, idx) => (
                    <div className="grid-3" key={idx}>
                      <div className="field" style={{ gridColumn: "1 / span 2" }}>
                        <label>Forma de pago #{idx + 1}</label>
                        <input
                          className="glass-input"
                          list="payment-presets"
                          value={fp}
                          onChange={(e) =>
                            updatePago(idx, e.target.value)
                          }
                        />
                      </div>

                      <div
                        className="field"
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          type="button"
                          className="glass-btn"
                          onClick={() => removeFormaPago(idx)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* TOTALES */}
                  <div className="grid-3">
                    <div className="field" style={{ gridColumn: "1 / span 2" }}>
                      <label className="checkbox-compact">
                        <input
                          type="checkbox"
                          checked={includeIban}
                          onChange={(e) => setIncludeIban(e.target.checked)}
                        />
                        <span>
                          Incluir IBAN en PDF
                          <br />
                          <small className="hint">
                            {company?.iban
                              ? `(${company.iban})`
                              : "Sin IBAN configurado"}
                          </small>
                        </span>
                      </label>
                    </div>

                    <div className="field">
                      <label style={{ opacity: 0.6 }}>Totales</label>

                      <div className="totals-box">
                        <div>
                          Base: <strong>{formatEuro(totales.base)}</strong>
                        </div>
                        <div>
                          IVA: <strong>{formatEuro(totales.iva)}</strong>
                        </div>
                        <div>
                          Total: <strong>{formatEuro(totales.total)}</strong>
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            paddingTop: 6,
                            borderTop: "1px dashed rgba(229,231,235,0.4)",
                            fontSize: 13,
                          }}
                        >
                          Pagos internos:{" "}
                          <strong>{formatEuro(pagosInternosTotal)}</strong>
                          <br />
                          Pendiente teórico:{" "}
                          <strong
                            style={{
                              color:
                                totales.total - pagosInternosTotal > 0
                                  ? "#f87171"
                                  : "#4ade80",
                            }}
                          >
                            {formatEuro(totales.total - pagosInternosTotal)}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PAGOS INTERNOS */}
                  <div className="section-head">
                    <div className="section-title">Pagos internos (no PDF)</div>

                    <button
                      type="button"
                      className="glass-btn"
                      onClick={addPagoInterno}
                    >
                      + Añadir pago
                    </button>
                  </div>

                  {form.pagos_internos.map((p, i) => (
                    <div className="pay-row" key={i}>
                      <div className="field">
                        <label>Fecha</label>
                        <input
                          type="date"
                          value={p.fecha}
                          onChange={(e) =>
                            updatePagoInterno(i, {
                              fecha: e.target.value,
                            })
                          }
                          className="glass-input"
                        />
                      </div>

                      <div className="field">
                        <label>Importe</label>
                        <input
                          type="text"
                          className="glass-input"
                          value={p.importe}
                          onChange={(e) =>
                            updatePagoInterno(i, {
                              importe: euro(parseEuro(e.target.value)),
                            })
                          }
                        />
                      </div>

                      <div className="field">
                        <label>Nota</label>
                        <input
                          className="glass-input"
                          value={p.nota}
                          onChange={(e) =>
                            updatePagoInterno(i, {
                              nota: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="field">
                        <button
                          type="button"
                          className="glass-btn"
                          onClick={() => removePagoInterno(i)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}

                </form>
              </div>

              {/* FOOTER */}
              <div className="modal-footer sticky-bottom footer-spaced">
                <button
                  type="button"
                  className="glass-btn"
                  onClick={close}
                  disabled={saving}
                >
                  Salir
                </button>

                <button
                  type="button"
                  className="glass-btn"
                  onClick={downloadPdf}
                  disabled={!isEditing}
                >
                  PDF
                </button>

                {isEditing &&
                  (form.estado === "aceptado" ||
                    form.estado === "pagado") && (
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={facturarDesdeModal}
                    >
                      Facturar
                    </button>
                  )}

                {isEditing && form.estado !== "aceptado" && (
                  <button
                    type="button"
                    className="glass-btn"
                    onClick={async () => {
                      if (!window.confirm("¿Marcar como ACEPTADO?")) return;
                      try {
                        const res = await fetch(
                          `${API}/presupuestos/${editingId}`,
                          {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ estado: "aceptado" }),
                          }
                        );
                        const json = await res.json();
                        if (json?.success) {
                          setList((prev) =>
                            prev.map((p) =>
                              String(p.id) === String(editingId)
                                ? json.data
                                : p
                            )
                          );
                          setForm((prev) => ({ ...prev, estado: "aceptado" }));
                        }
                      } catch (e) {
                        alert("Error cambiando estado");
                      }
                    }}
                  >
                    Aceptar Presupuesto
                  </button>
                )}

                <button
                  type="button"
                  className="glass-btn primary"
                  onClick={isEditing ? saveChanges : createBudget}
                  disabled={saving}
                >
                  {saving ? "Guardando…" : isEditing ? "Guardar" : "Crear y editar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
